
/**
 * AI-Enhanced Education Search Service
 * Integrates AI semantic analysis with existing Utdanning.no search
 * Maintains 100% backward compatibility with layered fallback strategy
 */

import { callDatabaseApi } from "./database-api-service";

export interface AIEducationAnalysis {
  primaryCategories: string[];
  secondaryCategories: string[];
  searchTerms: string[];
  confidence: number;
  reasoning: string;
  personalityTraits: string[];
  workPreferences: string[];
  suggestedCategories: string[];
  additionalContext: {
    aiEnhanced: boolean;
    semanticMappingUsed: boolean;
    multiCategorySearch: boolean;
  };
}

export interface AIEducationSearchResult {
  success: boolean;
  results: any[];
  metadata: {
    searchStrategy: 'ai-guided' | 'keyword-fallback' | 'category-fallback';
    aiAnalysisUsed: boolean;
    aiAnalysis?: AIEducationAnalysis;
    originalQuery: string;
    totalFound: number;
    queryTime: number;
  };
  formattedResponse: string;
}

/**
 * Call AI Parameter Interpreter for enhanced education semantic analysis
 */
async function getAIEducationAnalysis(query: string): Promise<AIEducationAnalysis | null> {
  console.log(`[AI-EDUCATION-SEARCH] 🤖 Getting enhanced AI analysis for: "${query}"`);
  
  try {
    const supabaseUrl = 'https://zxkxyapayniimhpjetaa.supabase.co';
    const response = await fetch(`${supabaseUrl}/functions/v1/ai-parameter-interpreter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp4a3h5YXBheW5paW1ocGpldGFhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc5OTUzMjIsImV4cCI6MjA2MzU3MTMyMn0.VV7odKx4YQIM9EfzATDhVqb-RAofnjTp1tDyXP9-eLc'
      },
      body: JSON.stringify({
        query: query,
        context: 'norwegian_education_semantic_mapping'
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`[AI-EDUCATION-SEARCH] ✅ Enhanced AI analysis successful with confidence: ${data.result?.confidence || 0}`);
      return data.result;
    } else {
      console.error(`[AI-EDUCATION-SEARCH] ❌ AI analysis failed: ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error(`[AI-EDUCATION-SEARCH] ❌ AI analysis error:`, error);
    return null;
  }
}

/**
 * Convert AI categories to intelligent search terms using comprehensive mapping
 */
function convertAICategoriestoSearchTerms(aiAnalysis: AIEducationAnalysis): string[] {
  const searchTerms = new Set<string>();
  
  // Add primary categories (highest priority)
  aiAnalysis.primaryCategories.forEach(cat => {
    searchTerms.add(cat.toLowerCase());
  });
  
  // Add secondary categories for broader coverage
  aiAnalysis.secondaryCategories.forEach(cat => {
    searchTerms.add(cat.toLowerCase());
  });
  
  // Add AI-identified search terms
  aiAnalysis.searchTerms.forEach(term => {
    searchTerms.add(term.toLowerCase());
  });
  
  // Add personality-based terms if relevant
  if (aiAnalysis.personalityTraits.length > 0) {
    aiAnalysis.personalityTraits.forEach(trait => {
      if (trait.includes('hjelpende') || trait.includes('omsorgsfullt')) {
        searchTerms.add('helse');
        searchTerms.add('barn');
      }
      if (trait.includes('kreativ') || trait.includes('kunstnerisk')) {
        searchTerms.add('kunst');
        searchTerms.add('design');
      }
      if (trait.includes('analytisk') || trait.includes('teknisk')) {
        searchTerms.add('teknologi');
        searchTerms.add('realfag');
      }
    });
  }
  
  return Array.from(searchTerms);
}

/**
 * Call existing Utdanning search via database API
 */
async function searchUtdanningViaAPI(query: string, maxResults: number = 10): Promise<any> {
  console.log(`[AI-EDUCATION-SEARCH] 🔍 Calling database API for: "${query}"`);
  
  try {
    const result = await callDatabaseApi({
      query: query,
      database: 'utdanning',
      maxResults: maxResults
    });
    
    console.log(`[AI-EDUCATION-SEARCH] ✅ Database API call successful`);
    return result;
  } catch (error) {
    console.error(`[AI-EDUCATION-SEARCH] ❌ Database API call failed:`, error);
    throw error;
  }
}

/**
 * AI-Enhanced Education Search with Intelligent Category Mapping
 */
export async function searchEducationWithAI(query: string, maxResults: number = 10): Promise<AIEducationSearchResult> {
  const startTime = Date.now();
  console.log(`[AI-EDUCATION-SEARCH] 🚀 Starting intelligent AI-enhanced search for: "${query}"`);
  
  let aiAnalysis: AIEducationAnalysis | null = null;
  let searchStrategy: 'ai-guided' | 'keyword-fallback' | 'category-fallback' = 'keyword-fallback';
  
  // FASE 1: Try intelligent AI-guided search with enhanced analysis
  try {
    aiAnalysis = await getAIEducationAnalysis(query);
    
    if (aiAnalysis && aiAnalysis.confidence > 0.6) {
      console.log(`[AI-EDUCATION-SEARCH] 🎯 Enhanced AI analysis successful, trying intelligent search`);
      console.log(`[AI-EDUCATION-SEARCH] 📊 AI insights:`, {
        primaryCategories: aiAnalysis.primaryCategories,
        personalityTraits: aiAnalysis.personalityTraits,
        workPreferences: aiAnalysis.workPreferences,
        confidence: aiAnalysis.confidence
      });
      
      // Convert AI categories to intelligent search terms
      const aiSearchTerms = convertAICategoriestoSearchTerms(aiAnalysis);
      const aiQuery = aiSearchTerms.join(' ');
      
      console.log(`[AI-EDUCATION-SEARCH] 🔍 Intelligent AI-guided query: "${aiQuery}"`);
      
      const aiResult = await searchUtdanningViaAPI(aiQuery, maxResults);
      
      if (aiResult.success && aiResult.results && aiResult.results.length > 0) {
        console.log(`[AI-EDUCATION-SEARCH] ✅ Intelligent AI-guided search successful: ${aiResult.results.length} results`);
        
        return {
          success: true,
          results: aiResult.results,
          metadata: {
            searchStrategy: 'ai-guided',
            aiAnalysisUsed: true,
            aiAnalysis,
            originalQuery: query,
            totalFound: aiResult.totalFound || aiResult.results.length,
            queryTime: Date.now() - startTime
          },
          // formattedResponse: enhanceFormattedResponseWithAI(aiResult.formattedResponse, aiAnalysis)
          formattedResponse: aiResult.formattedResponse
        };
      }
    } else {
      console.log(`[AI-EDUCATION-SEARCH] 🤔 AI analysis confidence too low (${aiAnalysis?.confidence || 0}), falling back`);
    }
  } catch (error) {
    console.error(`[AI-EDUCATION-SEARCH] ❌ AI-guided search failed:`, error);
  }
  
  // FASE 2: Fallback to existing keyword search
  console.log(`[AI-EDUCATION-SEARCH] 🔄 Falling back to existing keyword search`);
  
  try {
    const keywordResult = await searchUtdanningViaAPI(query, maxResults);
    
    if (keywordResult.success && keywordResult.results && keywordResult.results.length > 0) {
      console.log(`[AI-EDUCATION-SEARCH] ✅ Keyword search successful: ${keywordResult.results.length} results`);
      
      return {
        success: true,
        results: keywordResult.results,
        metadata: {
          searchStrategy: 'keyword-fallback',
          aiAnalysisUsed: false,
          aiAnalysis: aiAnalysis || undefined,
          originalQuery: query,
          totalFound: keywordResult.totalFound || keywordResult.results.length,
          queryTime: Date.now() - startTime
        },
        formattedResponse: keywordResult.formattedResponse
      };
    }
  } catch (error) {
    console.error(`[AI-EDUCATION-SEARCH] ❌ Keyword search failed:`, error);
  }
  
  // FASE 3: Final fallback - broad category search
  console.log(`[AI-EDUCATION-SEARCH] 🔄 Final fallback to broad category search`);
  
  try {
    const broadQuery = 'utdanning'; // Very broad search
    const broadResult = await searchUtdanningViaAPI(broadQuery, maxResults);
    
    return {
      success: broadResult.success,
      results: broadResult.results || [],
      metadata: {
        searchStrategy: 'category-fallback',
        aiAnalysisUsed: false,
        aiAnalysis: aiAnalysis || undefined,
        originalQuery: query,
        totalFound: broadResult.totalFound || (broadResult.results ? broadResult.results.length : 0),
        queryTime: Date.now() - startTime
      },
      formattedResponse: broadResult.formattedResponse || `## 🎓 Utdanningsalternativer

Jeg fant noen utdanningsalternativer, men søket kunne forbedres. Prøv å være mer spesifikk om hva du er interessert i.

**Søkte etter:** "${query}"

Besøk [Utdanning.no](https://utdanning.no) for å utforske alle utdanningsmuligheter.`
    };
  } catch (error) {
    console.error(`[AI-EDUCATION-SEARCH] ❌ All search strategies failed:`, error);
    
    return {
      success: false,
      results: [],
      metadata: {
        searchStrategy: 'category-fallback',
        aiAnalysisUsed: false,
        originalQuery: query,
        totalFound: 0,
        queryTime: Date.now() - startTime
      },
      formattedResponse: `## ❌ Feil ved søk i Utdanning.no

Søket feilet. Prøv igjen eller gå til [Utdanning.no](https://utdanning.no) direkte.

**Søkte etter:** "${query}"`
    };
  }
}

/**
 * Enhance formatted response with intelligent AI insights
 */
function enhanceFormattedResponseWithAI(originalResponse: string, aiAnalysis: AIEducationAnalysis): string {
  const insights = [];
  
  if (aiAnalysis.personalityTraits.length > 0) {
    insights.push(`**🧠 Personlighetstrekk identifisert:** ${aiAnalysis.personalityTraits.join(', ')}`);
  }
  
  if (aiAnalysis.workPreferences.length > 0) {
    insights.push(`**💼 Arbeidsønsker:** ${aiAnalysis.workPreferences.join(', ')}`);
  }
  
  if (aiAnalysis.primaryCategories.length > 0) {
    insights.push(`**🎯 Prioriterte kategorier:** ${aiAnalysis.primaryCategories.join(', ')}`);
  }
  
  const aiInsights = insights.length > 0 
    ? `\n\n### 🤖 Intelligent AI-analyse\n${insights.join('\n')}\n\n**Begrunnelse:** ${aiAnalysis.reasoning}`
    : '';
  
  return `${originalResponse}${aiInsights}\n\n---\n*🤖 Søk utført med intelligent AI-forbedret semantisk analyse (Confidence: ${Math.round(aiAnalysis.confidence * 100)}%)*`;
}
